import { DATE_FORMAT } from '@/helpers/dates';
import { Box, Button } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';

import { DataFilterOperation, FundType, ProgramType } from 'types';

import { useAccountDetailsContext } from './AccountDetailsContext';
import { extractFundKeyUsingTrancheName } from './extractFundKeyUsingTrancheName';
import { getIncentiveAllocationProperties } from './getIncentiveAllocationProperties';

interface UseAssetSalePropertiesParams {
  trancheDetails: Record<string, any>;
}

export function useAssetSaleProperties({ trancheDetails }: UseAssetSalePropertiesParams) {
  const { account, openModal } = useAccountDetailsContext();
  const accountIsHVAC = account?.programType === ProgramType.hvac;

  const generateTrancheLinks = (fundType: FundType) => {
    if (!account?.tranches) return '---';

    const filteredTranches = account.tranches.filter((tranche: string) => tranche.includes(fundType));

    if (filteredTranches.length === 0) return '---';

    return filteredTranches.map((tranche: string) => {
      const fundKey = extractFundKeyUsingTrancheName(tranche);
      const advancedFilters = encodeURIComponent(
        JSON.stringify([
          {
            id: 'tranche',
            operation: DataFilterOperation.Equal,
            selectedValues: [tranche],
          },
        ]),
      );

      return (
        <Link key={tranche} to={`/admin/funds/${fundKey}?advancedFilters=${advancedFilters}`}>
          {tranche}
        </Link>
      );
    });
  };

  const getTrancheStatus = (fundType: FundType) => {
    if (!account?.tranches || !trancheDetails) return '---';

    const matchingTranche = account.tranches.find((tranche: string) => tranche.includes(fundType));
    if (!matchingTranche) return '---';

    return trancheDetails[matchingTranche]?.trancheStatus || '---';
  };

  const getMatchingTranche = () => {
    if (!account?.tranches || !trancheDetails) return undefined;

    return (
      account.tranches.find((tranche: string) =>
        tranche.includes(accountIsHVAC ? FundType.hvacJv : FundType.taxEquity),
      ) ?? account.tranches.find((tranche: string) => tranche.includes(FundType.untrancheableAsset))
    );
  };

  const getTrancheDate = (): string => {
    const matchingTranche = getMatchingTranche();
    if (!matchingTranche) return '---';

    const trancheDate = trancheDetails[matchingTranche]?.trancheDate;
    if (!trancheDate) return '---';

    const dt = DateTime.fromISO(trancheDate);
    return dt.isValid ? dt.toFormat(DATE_FORMAT) : '---';
  };

  const getTrancheCloseDate = (): string => {
    const matchingTranche = getMatchingTranche();
    if (!matchingTranche) return '---';

    const trancheClosedDate = trancheDetails[matchingTranche]?.trancheClosedDate;
    if (!trancheClosedDate) return '---';

    return DateTime.fromISO(trancheClosedDate).toLocal().toFormat(DATE_FORMAT);
  };

  const hasUntrancheableAsset = account?.tranches?.some((tranche: string) =>
    tranche.includes(FundType.untrancheableAsset),
  );

  const assetSaleProperties = {
    heading: 'Asset Sale',
    showInternalOnly: true,
    values: [
      {
        label: accountIsHVAC ? 'HVAC JV' : 'Tax Equity',
        value: generateTrancheLinks(accountIsHVAC ? FundType.hvacJv : FundType.taxEquity),
      },
      {
        label: accountIsHVAC ? 'HVAC JV Status' : 'Tax Equity Status',
        value: getTrancheStatus(accountIsHVAC ? FundType.hvacJv : FundType.taxEquity),
      },
      {
        label: 'Debt Equity',
        value: generateTrancheLinks(FundType.debt),
      },
      {
        label: 'Debt Equity Status',
        value: getTrancheStatus(FundType.debt),
      },
      ...(hasUntrancheableAsset
        ? [
            {
              label: 'Untrancheable Asset',
              value: (
                <Box style={{ overflowX: 'auto', maxWidth: '250px' }}>
                  {generateTrancheLinks(FundType.untrancheableAsset)}
                </Box>
              ),
            },
            {
              label: 'Untrancheable Asset Status',
              value: getTrancheStatus(FundType.untrancheableAsset),
            },
          ]
        : []),
      {
        label: 'Accepted (aka Tranche Date)',
        value: getTrancheDate(),
      },
      {
        label: 'Tranche Close Date',
        value: getTrancheCloseDate(),
      },
      {
        label: 'Tranche History',
        value: (
          <Button variant="primary" tone="neutral" size="xs" onClick={() => openModal('trancheHistory')}>
            View History ({account?.trancheHistory?.length || 0})
          </Button>
        ),
      },
    ],
  };

  const allocationProperties = {
    heading: `Incentive Sales (${account?.incentiveAllocations?.currentAssignments?.length || 0})`,
    showInternalOnly: true,
    values: account ? getIncentiveAllocationProperties(account, () => openModal('incentivesHistory')) : [],
  };

  return { assetSaleProperties, allocationProperties };
}
